import { useEffect, useRef, useState } from "react";
import { http, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useBrand } from "../context/BrandContext";
import { Save, UserCog, Building2, Settings as Cog, Upload, Trash2, Mail } from "lucide-react";
import { MonthlyReportSettings } from "../components/MonthlyReportSettings";
import { PlanningStyleSettings } from "../components/PlanningStyleSettings";
import { PdfFooterSettings } from "../components/PdfFooterSettings";
import { PrivacySettings } from "../components/PrivacySettings";
import { RgpdRegistrySettings } from "../components/RgpdRegistrySettings";
import { LegalSettings } from "../components/LegalSettings";
import { PayrollSettings } from "../components/PayrollSettings";
import { MessageTemplatesSettings } from "../components/MessageTemplates";
import { MailLog } from "../components/MailLog";
import { DefaultLangCard } from "../components/DefaultLangCard";
import { OptionTrialsCard } from "../components/OptionTrialsCard";

const DEFAULT = {
  name: "", legal_name: "", siret: "", siren: "", naf: "", rcs: "", rcs_city: "", authorization_number: "", show_cnaps_legal_notice: true, tva_intracom: "",
  address: "", city: "", postal_code: "", country: "France",
  phone: "", email: "", website: "", iban: "", bic: "",
  default_vat_rate: 20, payment_terms_days: 40,
  default_hourly_rate_ht: 18, default_night_surcharge_pct: 10, default_sunday_surcharge_pct: 10, default_holiday_surcharge_pct: 100,
  min_billable_vacation_hours: 6,
  footer_notes: "", use_custom_footer: false,
  logo_file_id: null,
  signature_file_id: null,
  stamp_file_id: null,
  signature_show_on_invoices: true,
  resend_api_key: "", sender_email: "",
  smtp_host: "", smtp_port: 465, smtp_user: "", smtp_password: "", smtp_use_ssl: true,
  bcc_admin_email: "", bcc_admin_enabled: false,
  recruitment_email: "",
  recruitment_smtp_password: "",
  brand_tagline: "Sécurité privée",
  primary_color: "#0284c7",
};

export default function Settings() {
  const [c, setC] = useState(DEFAULT);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const { user, refresh } = useAuth();
  const { refresh: refreshBrand } = useBrand();
  const [me, setMe] = useState({ name: user?.name || "", email: user?.email || "", current_password: "", new_password: "" });
  const [meErr, setMeErr] = useState("");
  const [meMsg, setMeMsg] = useState("");
  const fileRef = useRef(null);
  const sigRef = useRef(null);
  const stampRef = useRef(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [sigUploading, setSigUploading] = useState(false);
  const [stampUploading, setStampUploading] = useState(false);
  // Cache-buster pour l'aperçu après upload
  const [sigVersion, setSigVersion] = useState(0);
  const [stampVersion, setStampVersion] = useState(0);

  useEffect(() => {
    http.get("/settings/company").then(r => setC({ ...DEFAULT, ...r.data }));
  }, []);
  useEffect(() => { setMe((m) => ({ ...m, name: user?.name || "", email: user?.email || "" })); }, [user]);

  const uploadLogo = async (file) => {
    if (!file) return;
    setLogoUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await http.post("/settings/company/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setC((cc) => ({ ...cc, logo_file_id: r.data.id }));
      await refreshBrand();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setLogoUploading(false); }
  };
  const removeLogo = async () => {
    if (!c.logo_file_id) return;
    await http.delete(`/files/${c.logo_file_id}`);
    const next = { ...c, logo_file_id: null };
    setC(next);
    await http.put("/settings/company", next);
    await refreshBrand();
  };

  const uploadCompanyImage = async (endpoint, file, setter, setUploading, versionSetter) => {
    if (!file) return;
    setUploading(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await http.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setC((cc) => ({ ...cc, [setter]: r.data.id }));
      versionSetter?.((v) => v + 1);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setUploading(false); }
  };
  const uploadSignature = (f) => uploadCompanyImage("/settings/company/signature", f, "signature_file_id", setSigUploading, setSigVersion);
  const uploadStamp = (f) => uploadCompanyImage("/settings/company/stamp", f, "stamp_file_id", setStampUploading, setStampVersion);
  const removeSignature = async () => {
    await http.delete("/settings/company/signature");
    setC((cc) => ({ ...cc, signature_file_id: null }));
  };
  const removeStamp = async () => {
    await http.delete("/settings/company/stamp");
    setC((cc) => ({ ...cc, stamp_file_id: null }));
  };

  const saveCompany = async () => {
    setSaving(true); setMsg(""); setErr("");
    try {
      await http.put("/settings/company", c);
      await refreshBrand();
      setMsg("Enregistré."); setTimeout(()=>setMsg(""), 2500);
    }
    catch (e) { setErr(formatApiError(e)); }
    finally { setSaving(false); }
  };
  const saveMe = async () => {
    setMeErr(""); setMeMsg("");
    try {
      const body = {};
      if (me.name !== user?.name) body.name = me.name;
      if (me.email !== user?.email) body.email = me.email;
      if (me.new_password) body.new_password = me.new_password;
      if (me.new_password || body.email) body.current_password = me.current_password;
      if (Object.keys(body).length === 0) { setMeMsg("Rien à modifier."); return; }
      await http.patch("/auth/me", body);
      await refresh();
      setMe({ ...me, current_password: "", new_password: "" });
      setMeMsg("Compte mis à jour.");
      setTimeout(()=>setMeMsg(""), 2500);
    } catch (e) { setMeErr(formatApiError(e)); }
  };

  return (
    <div className="p-8 max-w-4xl mx-auto" data-testid="settings-page">
      <div className="mb-6">
        <h1 className="font-display text-4xl font-extrabold tracking-tight">Paramètres</h1>
        <p className="text-sm text-slate-600">Informations société et compte administrateur.</p>
      </div>

      <OptionTrialsCard />
      <DefaultLangCard />

      <div className="border border-slate-200 bg-white rounded-md p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Building2 className="h-5 w-5 text-sky-600"/><h2 className="font-display text-xl font-bold">Informations société</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <F label="Nom commercial *"><input data-testid="company-name" className="input" value={c.name||""} onChange={(e)=>setC({...c, name:e.target.value})}/></F>
          <F label="Sous-titre (affiché sous le logo)"><input data-testid="brand-tagline" className="input" value={c.brand_tagline||""} onChange={(e)=>setC({...c, brand_tagline:e.target.value})} placeholder="Sécurité privée"/></F>
          <F label="Couleur principale (boutons, focus, accents)">
            <div className="flex items-center gap-3">
              <input
                data-testid="primary-color-picker"
                type="color"
                className="h-10 w-14 border border-slate-300 rounded cursor-pointer"
                value={c.primary_color || "#0284c7"}
                onChange={(e)=>setC({...c, primary_color:e.target.value})}
              />
              <input
                data-testid="primary-color-hex"
                type="text"
                className="input flex-1"
                value={c.primary_color || "#0284c7"}
                onChange={(e)=>setC({...c, primary_color:e.target.value})}
                placeholder="#0284c7"
                maxLength={7}
              />
              <button
                type="button"
                onClick={()=>setC({...c, primary_color:"#0284c7"})}
                className="text-xs text-slate-500 hover:text-slate-900 underline whitespace-nowrap"
                title="Restaurer la couleur par défaut"
              >Défaut</button>
            </div>
          </F>
          <F label="Raison sociale"><input className="input" value={c.legal_name||""} onChange={(e)=>setC({...c, legal_name:e.target.value})}/></F>
          <div className="md:col-span-2">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">Logo de la société (png/jpg/webp · max 2 Mo)</div>
            <div className="flex items-center gap-4">
              <div className="h-20 w-20 border-2 border-dashed border-slate-300 rounded-md grid place-items-center bg-slate-50 overflow-hidden">
                {c.logo_file_id
                  ? <img src={`${process.env.REACT_APP_BACKEND_URL}/api/brand/logo?v=${c.logo_file_id}`} alt="logo" className="h-full w-full object-contain"/>
                  : <span className="text-[10px] text-slate-400 uppercase tracking-widest font-bold">Aucun</span>}
              </div>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e)=>uploadLogo(e.target.files?.[0])}/>
              <button data-testid="logo-upload" type="button" disabled={logoUploading} onClick={()=>fileRef.current?.click()} className="inline-flex items-center gap-2 border border-slate-300 rounded-md px-3 py-2 text-xs font-semibold hover:bg-slate-50 disabled:opacity-60"><Upload className="h-3.5 w-3.5"/>{logoUploading?"Upload…":"Téléverser"}</button>
              {c.logo_file_id && <button type="button" onClick={removeLogo} className="inline-flex items-center gap-2 text-rose-600 hover:text-rose-700 text-xs font-semibold"><Trash2 className="h-3.5 w-3.5"/>Retirer</button>}
            </div>
          </div>

          {/* Signature + Cachet de l'entreprise */}
          <div className="md:col-span-2 border-t border-slate-200 pt-4 mt-2">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Signature et cachet de l'entreprise</div>
            <p className="text-xs text-slate-500 mb-3">
              Ces images seront apposées automatiquement sur tous les documents PDF que vous générez :
              contrats agents (case « Pour l'Employeur »), devis, factures et attestations.
              Format recommandé : PNG avec fond transparent, max 2 Mo.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Signature */}
              <div className="border border-slate-200 rounded-md p-3">
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-600 mb-2">Signature du responsable</div>
                <div className="flex items-center gap-4">
                  <div className="h-20 w-40 border-2 border-dashed border-slate-300 rounded-md grid place-items-center bg-white overflow-hidden">
                    {c.signature_file_id
                      ? <img src={`${process.env.REACT_APP_BACKEND_URL}/api/settings/company/signature/preview?v=${c.signature_file_id}_${sigVersion}`} alt="signature" className="h-full w-full object-contain"/>
                      : <span className="text-[10px] text-slate-400 uppercase tracking-widest font-bold">Aucune</span>}
                  </div>
                  <div className="flex flex-col gap-1">
                    <input ref={sigRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e)=>uploadSignature(e.target.files?.[0])}/>
                    <button data-testid="signature-upload" type="button" disabled={sigUploading} onClick={()=>sigRef.current?.click()} className="inline-flex items-center gap-2 border border-slate-300 rounded-md px-3 py-1.5 text-xs font-semibold hover:bg-slate-50 disabled:opacity-60">
                      <Upload className="h-3.5 w-3.5"/>{sigUploading?"Upload…":"Téléverser"}
                    </button>
                    {c.signature_file_id && <button data-testid="signature-remove" type="button" onClick={removeSignature} className="inline-flex items-center gap-2 text-rose-600 hover:text-rose-700 text-xs font-semibold px-3 py-1"><Trash2 className="h-3.5 w-3.5"/>Retirer</button>}
                  </div>
                </div>
              </div>

              {/* Cachet */}
              <div className="border border-slate-200 rounded-md p-3">
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-600 mb-2">Cachet / Tampon de l'entreprise</div>
                <div className="flex items-center gap-4">
                  <div className="h-20 w-20 border-2 border-dashed border-slate-300 rounded-md grid place-items-center bg-white overflow-hidden">
                    {c.stamp_file_id
                      ? <img src={`${process.env.REACT_APP_BACKEND_URL}/api/settings/company/stamp/preview?v=${c.stamp_file_id}_${stampVersion}`} alt="cachet" className="h-full w-full object-contain"/>
                      : <span className="text-[10px] text-slate-400 uppercase tracking-widest font-bold">Aucun</span>}
                  </div>
                  <div className="flex flex-col gap-1">
                    <input ref={stampRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e)=>uploadStamp(e.target.files?.[0])}/>
                    <button data-testid="stamp-upload" type="button" disabled={stampUploading} onClick={()=>stampRef.current?.click()} className="inline-flex items-center gap-2 border border-slate-300 rounded-md px-3 py-1.5 text-xs font-semibold hover:bg-slate-50 disabled:opacity-60">
                      <Upload className="h-3.5 w-3.5"/>{stampUploading?"Upload…":"Téléverser"}
                    </button>
                    {c.stamp_file_id && <button data-testid="stamp-remove" type="button" onClick={removeStamp} className="inline-flex items-center gap-2 text-rose-600 hover:text-rose-700 text-xs font-semibold px-3 py-1"><Trash2 className="h-3.5 w-3.5"/>Retirer</button>}
                  </div>
                </div>
              </div>
            </div>
            <label className="flex items-center gap-2 mt-3 text-sm text-slate-700 select-none">
              <input data-testid="signature-show-invoices" type="checkbox" className="h-4 w-4" checked={c.signature_show_on_invoices !== false}
                     onChange={(e)=>setC({...c, signature_show_on_invoices: e.target.checked})}/>
              <span>Afficher la signature et le cachet sur les <b>devis et factures</b> (toujours affichés sur les contrats)</span>
            </label>
          </div>
          <F label="SIREN (9 chiffres)"><input data-testid="company-siren" className="input font-mono" maxLength={9} placeholder="123456789" value={c.siren||""} onChange={(e)=>setC({...c, siren:e.target.value.replace(/\D/g,"")})}/></F>
          <F label="SIRET (14 chiffres)"><input className="input font-mono" value={c.siret||""} onChange={(e)=>setC({...c, siret:e.target.value})}/></F>
          <F label="Code NAF/APE"><input className="input font-mono" placeholder="8010Z" value={c.naf||""} onChange={(e)=>setC({...c, naf:e.target.value})}/></F>
          <F label="RCS N°"><input className="input" value={c.rcs||""} onChange={(e)=>setC({...c, rcs:e.target.value})}/></F>
          <F label="RCS ville (ex: Rouen)"><input className="input" placeholder="Rouen" value={c.rcs_city||""} onChange={(e)=>setC({...c, rcs_city:e.target.value})}/></F>
          <F label="N° TVA intracom"><input className="input" value={c.tva_intracom||""} onChange={(e)=>setC({...c, tva_intracom:e.target.value})}/></F>
          <div className="md:col-span-2"><F label="Autorisation d'exercice CNAPS (AUT-…)"><input data-testid="auth-number" className="input font-mono" placeholder="AUT-076-2124-07-07-20250987675" value={c.authorization_number||""} onChange={(e)=>setC({...c, authorization_number:e.target.value})}/></F></div>
          <F label="Téléphone"><input className="input" value={c.phone||""} onChange={(e)=>setC({...c, phone:e.target.value})}/></F>
          <F label="Email"><input className="input" value={c.email||""} onChange={(e)=>setC({...c, email:e.target.value})}/></F>
          <F label="Site web"><input className="input" value={c.website||""} onChange={(e)=>setC({...c, website:e.target.value})}/></F>
          <div className="md:col-span-2"><F label="Adresse"><input className="input" value={c.address||""} onChange={(e)=>setC({...c, address:e.target.value})}/></F></div>
          <F label="Code postal"><input className="input" value={c.postal_code||""} onChange={(e)=>setC({...c, postal_code:e.target.value})}/></F>
          <F label="Ville"><input className="input" value={c.city||""} onChange={(e)=>setC({...c, city:e.target.value})}/></F>
          <F label="Pays"><select data-no-i18n className="input" data-testid="company-country" value={/royaume|united kingdom/i.test(c.country||"") ? "Royaume-Uni" : /suisse|switzerland/i.test(c.country||"") ? "Suisse" : "France"}
            onChange={(e)=>setC({...c, country:e.target.value, default_vat_rate: { "France": 20, "Royaume-Uni": 20, "Suisse": 8.1 }[e.target.value]})}>
            <option value="France">France (€, CNAPS)</option><option value="Royaume-Uni">Royaume-Uni (£, licence SIA)</option><option value="Suisse">Suisse (CHF, autorisation cantonale)</option>
          </select></F>
          <F label="IBAN"><input className="input" value={c.iban||""} onChange={(e)=>setC({...c, iban:e.target.value})}/></F>
          <F label="BIC"><input className="input" value={c.bic||""} onChange={(e)=>setC({...c, bic:e.target.value})}/></F>
        </div>

        <div className="mt-6 pt-6 border-t border-slate-200">
          <div className="flex items-center gap-2 mb-3"><Cog className="h-4 w-4 text-slate-500"/><h3 className="font-display text-sm font-bold uppercase tracking-widest text-slate-600">Valeurs par défaut</h3></div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <F label="TVA (%)"><input type="number" step="0.1" className="input" value={c.default_vat_rate} onChange={(e)=>setC({...c, default_vat_rate:parseFloat(e.target.value||0)})}/></F>
            <F label="Échéance (jours)"><input type="number" className="input" value={c.payment_terms_days} onChange={(e)=>setC({...c, payment_terms_days:parseInt(e.target.value||0)})}/></F>
            <F label="Tarif horaire HT (€)"><input type="number" step="0.01" className="input" value={c.default_hourly_rate_ht} onChange={(e)=>setC({...c, default_hourly_rate_ht:parseFloat(e.target.value||0)})}/></F>
            <F label="Majo. nuit 21-06h (%)"><input type="number" step="0.1" className="input" value={c.default_night_surcharge_pct} onChange={(e)=>setC({...c, default_night_surcharge_pct:parseFloat(e.target.value||0)})}/></F>
            <F label="Majo. dimanche (%)"><input type="number" step="0.1" className="input" value={c.default_sunday_surcharge_pct} onChange={(e)=>setC({...c, default_sunday_surcharge_pct:parseFloat(e.target.value||0)})}/></F>
            <F label="Majo. fériés (%)"><input type="number" step="0.1" className="input" value={c.default_holiday_surcharge_pct} onChange={(e)=>setC({...c, default_holiday_surcharge_pct:parseFloat(e.target.value||0)})}/></F>
            <F label="Plancher min. facturable (h/vacation)"><input data-testid="min-vac-hours" type="number" step="0.5" min="0" className="input" value={c.min_billable_vacation_hours ?? 6} onChange={(e)=>setC({...c, min_billable_vacation_hours:parseFloat(e.target.value||0)})}/></F>
          </div>
        </div>

        <div className="mt-6 border border-slate-200 rounded-md p-4 bg-slate-50/40">
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-600 mb-2">Pied de page des documents (contrats, devis, factures)</div>
          <p className="text-[11px] text-slate-500 mb-3">
            Par défaut, le pied de page est <b>généré automatiquement</b> à partir des champs ci-dessus (<i>Raison sociale · RCS · APE · TVA · Autorisation d'exercice</i>). Vous pouvez activer un <b>texte personnalisé</b> pour remplacer cette ligne.
          </p>
          <label className="flex items-center gap-2 mb-2 text-sm text-slate-700 select-none">
            <input data-testid="show-cnaps-notice" type="checkbox" className="h-4 w-4" checked={c.show_cnaps_legal_notice !== false}
                   onChange={(e)=>setC({...c, show_cnaps_legal_notice:e.target.checked})}/>
            <span>Afficher la mention légale <b>Art. L612-14 CSI</b> (obligatoire sécurité privée)</span>
          </label>
          <label className="flex items-center gap-2 mb-3 text-sm text-slate-700 select-none">
            <input data-testid="use-custom-footer" type="checkbox" className="h-4 w-4" checked={!!c.use_custom_footer}
                   onChange={(e)=>setC({...c, use_custom_footer:e.target.checked})}/>
            <span>Utiliser le <b>texte personnalisé</b> ci-dessous à la place des mentions légales auto</span>
          </label>
          <F label={c.use_custom_footer ? "Texte du pied de page (actif)" : "Texte du pied de page (actif uniquement si case cochée ou si aucune mention structurée)"}>
            <textarea data-testid="footer-notes" rows={2} className="input" value={c.footer_notes||""} onChange={(e)=>setC({...c, footer_notes:e.target.value})} placeholder="Ex : SARL au capital de 10 000 € — RCS Paris B 123 456 789 — APE 8010Z"/>
          </F>
        </div>

        <div className="mt-6 pt-6 border-t border-slate-200">
          <div className="flex items-center gap-2 mb-3"><Mail className="h-4 w-4 text-sky-600"/><h3 className="font-display text-sm font-bold uppercase tracking-widest text-slate-600">Envoi des emails</h3></div>
          <p className="text-xs text-slate-500 mb-4">Deux modes possibles — <b>SMTP est utilisé en priorité si configuré</b>. Sinon, l'app bascule sur Resend.</p>

          {/* SMTP direct (Hostinger, OVH, Gmail…) */}
          <div className="border border-slate-200 rounded-md p-4 bg-slate-50/50 mb-4">
            <div className="text-xs font-bold text-slate-700 mb-1">Mode 1 — SMTP direct (recommandé pour Hostinger/OVH)</div>
            <p className="text-[11px] text-slate-500 mb-3">Utilisez votre boîte mail professionnelle. Pour Hostinger : hôte <code>smtp.hostinger.com</code>, port <code>465</code> (SSL) ou <code>587</code> (TLS).</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <F label="Serveur SMTP"><input data-testid="smtp-host" className="input" value={c.smtp_host||""} onChange={(e)=>setC({...c, smtp_host:e.target.value})} placeholder="smtp.hostinger.com"/></F>
              <div className="grid grid-cols-2 gap-2">
                <F label="Port"><input data-testid="smtp-port" type="number" className="input" value={c.smtp_port||465} onChange={(e)=>setC({...c, smtp_port:parseInt(e.target.value||"465",10)})} placeholder="465"/></F>
                <F label="SSL/TLS">
                  <select data-testid="smtp-ssl" className="input" value={c.smtp_use_ssl ? "ssl" : "tls"} onChange={(e)=>setC({...c, smtp_use_ssl: e.target.value === "ssl"})}>
                    <option value="ssl">SSL (port 465)</option>
                    <option value="tls">STARTTLS (port 587)</option>
                  </select>
                </F>
              </div>
              <F label="Identifiant (email)"><input data-testid="smtp-user" className="input" value={c.smtp_user||""} onChange={(e)=>setC({...c, smtp_user:e.target.value})} placeholder="contact@votre-entreprise.fr"/></F>
              <F label={c.smtp_password_set ? "Mot de passe (enregistré — laisser vide pour ne pas modifier)" : "Mot de passe"}>
                <input
                  data-testid="smtp-password"
                  type="password"
                  className="input"
                  value={c.smtp_password||""}
                  onChange={(e)=>setC({...c, smtp_password:e.target.value})}
                  placeholder={c.smtp_password_set ? "•••••••• (déjà enregistré)" : "Mot de passe de la boîte mail"}
                  autoComplete="new-password"
                />
              </F>
            </div>
          </div>

          {/* Resend en fallback */}
          <div className="border border-slate-200 rounded-md p-4 bg-slate-50/50">
            <div className="text-xs font-bold text-slate-700 mb-1">Mode 2 — Resend (alternative)</div>
            <p className="text-[11px] text-slate-500 mb-3">Créez une clé sur <a href="https://resend.com/api-keys" target="_blank" rel="noreferrer" className="text-sky-700 hover:underline">resend.com/api-keys</a> et vérifiez votre domaine.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <F label={c.resend_api_key_set ? "Clé API Resend (enregistrée — laisser vide pour ne pas modifier)" : "Clé API Resend"}>
                <input
                  data-testid="resend-key"
                  type="password"
                  className="input"
                  value={c.resend_api_key||""}
                  onChange={(e)=>setC({...c, resend_api_key:e.target.value})}
                  placeholder={c.resend_api_key_set ? "•••••••• (déjà enregistrée)" : "re_xxxxxxxxxxxx"}
                  autoComplete="new-password"
                />
              </F>
              <F label="Email expéditeur commun"><input data-testid="sender-email" type="email" className="input" value={c.sender_email||""} onChange={(e)=>setC({...c, sender_email:e.target.value})} placeholder="contact@votre-entreprise.fr"/></F>
            </div>
            {c.resend_api_key && !c.resend_api_key.startsWith("re_") && <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mt-2">⚠ Une clé Resend valide commence par <code>re_</code>.</div>}
          </div>

          {/* Copie cachée admin (Cci) sur tous les envois */}
          <div className="mt-4 border border-slate-200 rounded-md p-4 bg-slate-50/50">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                data-testid="bcc-admin-enabled"
                type="checkbox"
                className="h-4 w-4"
                checked={!!c.bcc_admin_enabled}
                onChange={(e)=>setC({...c, bcc_admin_enabled: e.target.checked})}
              />
              <span className="text-xs font-bold text-slate-700">Mettre l'administrateur en copie cachée (Cci) de tous les emails envoyés par l'application</span>
            </label>
            <p className="text-[11px] text-slate-500 mt-1 ml-6">Pratique pour garder une trace des échanges dans votre boîte mail. Le destinataire ne verra pas cette adresse.</p>
            {c.bcc_admin_enabled && (
              <div className="mt-3 ml-6">
                <F label="Adresse email en Cci">
                  <input
                    data-testid="bcc-admin-email"
                    type="email"
                    className="input"
                    value={c.bcc_admin_email||""}
                    onChange={(e)=>setC({...c, bcc_admin_email:e.target.value})}
                    placeholder={user?.email || "admin@votre-entreprise.fr"}
                  />
                </F>
              </div>
            )}
          </div>

          {/* Adresse dédiée pour les candidatures */}
          <div className="mt-4 border border-sky-200 rounded-md p-4 bg-sky-50/40">
            <div className="text-xs font-bold text-slate-700 mb-1">📥 Adresse dédiée aux candidatures</div>
            <p className="text-[11px] text-slate-600 mb-3">
              Adresse utilisée pour <b>recevoir les nouvelles candidatures</b> soumises via <code>/postuler</code>,
              et <b>expéditeur</b> des emails de confirmation envoyés aux candidats.
              <br/>
              Chez Hostinger, chaque boîte a ses propres identifiants SMTP : renseignez le <b>mot de passe SMTP de la boîte {c.recruitment_email || "de recrutement"}</b> ci-dessous pour envoyer réellement depuis cette adresse. Si laissé vide, l'app utilisera le SMTP principal ci-dessus avec un simple <code>Reply-To</code>.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <F label="Email recrutement">
                <input
                  data-testid="recruitment-email"
                  type="email"
                  className="input"
                  value={c.recruitment_email||""}
                  onChange={(e)=>setC({...c, recruitment_email: e.target.value})}
                  placeholder="recrutement@votre-entreprise.fr"
                />
              </F>
              <F label={c.recruitment_smtp_password_set ? "Mot de passe SMTP recrutement (enregistré — laisser vide pour ne pas modifier)" : "Mot de passe SMTP de la boîte recrutement"}>
                <input
                  data-testid="recruitment-smtp-password"
                  type="password"
                  className="input"
                  value={c.recruitment_smtp_password||""}
                  onChange={(e)=>setC({...c, recruitment_smtp_password: e.target.value})}
                  placeholder={c.recruitment_smtp_password_set ? "•••••••• (déjà enregistré)" : "Mot de passe SMTP de recrutement@…"}
                  autoComplete="new-password"
                />
              </F>
            </div>
            {c.recruitment_smtp_password_set && (
              <div className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 mt-2">
                ✅ Le compte SMTP dédié <b>{c.recruitment_email}</b> est configuré. Les emails de candidature partiront réellement depuis cette adresse.
              </div>
            )}
          </div>

          <div className="mt-4 border border-rose-200 rounded-md p-4 bg-rose-50/40">
            <div className="text-xs font-bold text-slate-700 mb-1">Alertes CNAPS — services destinataires</div>
            <p className="text-[11px] text-slate-600 mb-3">En cas de carte professionnelle non valide, l&apos;alerte est envoyée à l&apos;administrateur principal et à ces adresses.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <F label="Email service Planning"><input data-testid="planning-email" type="email" className="input" value={c.planning_email||""} onChange={(e)=>setC({...c, planning_email: e.target.value})} placeholder="planning@…" /></F>
              <F label="Email service Exploitation"><input data-testid="exploitation-email" type="email" className="input" value={c.exploitation_email||""} onChange={(e)=>setC({...c, exploitation_email: e.target.value})} placeholder="exploitation@…" /></F>
            </div>
          </div>

          <div className="mt-3">
            <button
              data-testid="test-email-btn"
              onClick={async ()=>{
                setErr(""); setMsg("");
                try {
                  const r = await http.post("/settings/test-email", { to: (c.smtp_user || c.sender_email || user?.email) });
                  if (r.data.sent) setMsg(`Email de test envoyé via ${r.data.provider} !`);
                  else setErr(`Échec : ${r.data.reason || "raison inconnue"}`);
                } catch (e) { setErr(e?.response?.data?.detail || "Erreur"); }
              }}
              className="text-xs font-semibold px-3 py-1.5 rounded-md border border-slate-300 hover:bg-white"
            >
              ✉ Envoyer un email de test
            </button>
            <span className="text-[11px] text-slate-500 ml-2">Un mail arrivera dans votre boîte pour valider la config avant d'enregistrer.</span>
          </div>
        </div>
        {err && <div className="mt-4 text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{err}</div>}
        {msg && <div className="mt-4 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2">{msg}</div>}
        <div className="mt-6 flex justify-end">
          <button data-testid="save-company" onClick={saveCompany} disabled={saving} className="inline-flex items-center gap-2 hover:opacity-90 text-white px-4 py-2 rounded-md text-sm font-semibold disabled:opacity-70" style={{ backgroundColor: "var(--brand-primary)" }}><Save className="h-4 w-4"/>{saving?"…":"Enregistrer"}</button>
        </div>
      </div>

      {user?.role === "admin" && <MonthlyReportSettings />}

      {user?.role === "admin" && <PlanningStyleSettings />}

      {user?.role === "admin" && <PdfFooterSettings />}
      {user?.role === "admin" && <PrivacySettings />}
      {user?.role === "admin" && <RgpdRegistrySettings />}
      {user?.role === "admin" && <LegalSettings />}
      {user?.role === "admin" && <PayrollSettings />}
      <MessageTemplatesSettings />
      {user?.role === "admin" && <MailLog endpoint="/mail-log" />}

      <div className="border border-slate-200 bg-white rounded-md p-6">
        <div className="flex items-center gap-2 mb-4">
          <UserCog className="h-5 w-5 text-sky-600"/><h2 className="font-display text-xl font-bold">Mon compte</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <F label="Nom"><input className="input" value={me.name} onChange={(e)=>setMe({...me, name:e.target.value})}/></F>
          <F label="Email de connexion *"><input data-testid="me-email" type="email" className="input" value={me.email} onChange={(e)=>setMe({...me, email:e.target.value})}/></F>
          <F label="Mot de passe actuel"><input data-testid="me-current-password" type="password" className="input" value={me.current_password} onChange={(e)=>setMe({...me, current_password:e.target.value})} placeholder="Requis pour changer l'email ou le mot de passe"/></F>
          <F label="Nouveau mot de passe"><input data-testid="me-new-password" type="password" className="input" value={me.new_password} onChange={(e)=>setMe({...me, new_password:e.target.value})} placeholder="10 caractères min., lettres et chiffres"/></F>
        </div>
        {meErr && <div data-testid="me-error" className="mt-4 text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{meErr}</div>}
        {meMsg && <div className="mt-4 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2">{meMsg}</div>}
        <div className="mt-6 flex justify-end">
          <button data-testid="save-me" onClick={saveMe} className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 rounded-md text-sm font-semibold"><Save className="h-4 w-4"/>Mettre à jour</button>
        </div>
      </div>

      <style>{`.input{width:100%;border:1px solid #cbd5e1;border-radius:6px;padding:.55rem .7rem;font-size:.875rem;outline:none;background:white}.input:focus{border-color:#0284c7;box-shadow:0 0 0 3px #0ea5e933}`}</style>
    </div>
  );
}

function F({ label, children }) { return <label className="block"><div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">{label}</div>{children}</label>; }
